"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { WaitlistForm } from "@/components/waitlist-form"
import { SiteHeader } from "@/components/site-header"
import { SectionLabel } from "@/components/section-label"
import { LocaleSuggestionBanner } from "@/components/locale-suggestion-banner"
import { Zap, Headphones, MessageSquare, ArrowRight, Shield } from "lucide-react"
import { useTranslations } from "next-intl"
import { Link } from "@/i18n/navigation"
import { SiteFooter } from "@/components/site-footer"
import { motion } from "framer-motion"

// Animation variants
const easeOut = [0.16, 1, 0.3, 1] as const

const fadeInUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeOut } }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: easeOut } }
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
}

export default function WaitlistPage() {
  const t = useTranslations()
  const tWaitlist = useTranslations("waitlist")

  // Navigation links
  const navigationLinks = [
    { name: t("nav.features"), href: "/#features" },
    { name: t("nav.pricing"), href: "/#pricing" },
    { name: t("nav.about"), href: "/about" },
    { name: t("nav.contact"), href: "/impressum" },
  ]

  // Footer links
  const footerLinks = {
    product: [
      { name: t("footer.links.features"), href: "/#features" },
      { name: t("footer.links.pricing"), href: "/#pricing" },
    ],
    company: [
      { name: t("footer.links.about"), href: "/about" },
      { name: t("footer.links.contact"), href: "/impressum" },
    ],
    legal: [
      { name: t("footer.links.privacy"), href: "/privacy" },
      { name: t("footer.links.terms"), href: "/terms" },
      { name: t("footer.links.gtc"), href: "/gtc" },
      { name: t("footer.links.betaTerms"), href: "/beta-terms" },
    ],
  }

  const benefits = [
    {
      icon: Zap,
      title: tWaitlist("benefits.earlyAccess"),
      description: tWaitlist("benefits.earlyAccessDescription"),
    },
    {
      icon: Shield,
      title: tWaitlist("benefits.gdprCompliance"),
      description: tWaitlist("benefits.gdprComplianceDescription"),
    },
    {
      icon: Headphones,
      title: tWaitlist("benefits.prioritySupport"),
      description: tWaitlist("benefits.prioritySupportDescription"),
    },
    {
      icon: MessageSquare,
      title: tWaitlist("benefits.featureInput"),
      description: tWaitlist("benefits.featureInputDescription"),
    },
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-100 focus:px-4 focus:py-2 focus:bg-accent focus:text-accent-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
      >
        {t("common.skipToContent")}
      </a>

      {/* Locale Suggestion Banner */}
      <LocaleSuggestionBanner />

      {/* Header */}
      <SiteHeader navigationLinks={navigationLinks} />

      <main id="main-content">
      {/* Hero Section */}
      <section className="py-28 md:py-44 relative overflow-hidden" aria-labelledby="hero-heading">
        <div className="max-w-6xl px-4 md:px-6 mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="flex flex-col items-center text-center space-y-8"
          >
            <motion.div variants={fadeInUp}>
              <Badge variant="secondary" className="px-4 py-1">
                {tWaitlist("subtitle")}
              </Badge>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <h1 id="hero-heading" className="text-[2.75rem] md:text-6xl lg:text-[5.25rem] font-normal tracking-tight leading-[1.2]">
                {tWaitlist("title")}
              </h1>
            </motion.div>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              {tWaitlist("description")}
            </motion.p>
            <motion.div variants={fadeInUp} className="w-full max-w-md">
              <WaitlistForm />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-28 md:py-44 bg-muted" aria-labelledby="benefits-heading">
        <div className="max-w-6xl px-4 md:px-6 mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15, margin: "-60px" }}
            variants={stagger}
            className="text-center space-y-4 mb-16"
          >
            <motion.div variants={fadeInUp}>
              <SectionLabel label={tWaitlist("benefits.title")} />
            </motion.div>
            <motion.h2 id="benefits-heading" variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[3.25rem] font-normal leading-[1.15]">
              {tWaitlist("benefits.title")}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15, margin: "-60px" }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full text-center">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-linear-to-br from-accent to-accent-secondary flex items-center justify-center shadow-accent mb-4 mx-auto">
                      <benefit.icon className="h-7 w-7 text-accent-foreground" aria-hidden="true" />
                    </div>
                    <CardTitle className="text-lg font-semibold">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 md:py-44 bg-background" aria-labelledby="cta-heading">
        <div className="max-w-6xl px-4 md:px-6 mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15, margin: "-60px" }}
            variants={stagger}
            className="text-center space-y-8"
          >
            <motion.h2 id="cta-heading" variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[3.25rem] font-normal leading-[1.15]">
              {tWaitlist("cta.title")}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tWaitlist("cta.description")}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="outline">
                <Link href="/about">
                  {tWaitlist("cta.learnMore")}
                </Link>
              </Button>
              <Button asChild size="lg">
                <Link href="/">
                  {tWaitlist("cta.backToHome")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <SiteFooter footerLinks={footerLinks} />
    </div>
  )
}

